import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { useWorkspaceStore } from '@/stores/workspace';
import { BackButton } from '@/components/BackButton';
import { ListCard } from '@/components/ui/ListCard';
import { toast } from '@/lib/toast';

interface MemberRow {
  id: string;
  email: string;
  name: string | null;
  role: 'owner' | 'member';
}

export function WorkspacePage() {
  const { t } = useTranslation();
  const ws = useWorkspaceStore((s) => s.current);

  const { data: members } = useQuery({
    queryKey: ['members', ws?.id],
    queryFn: () => api.get<MemberRow[]>(`/workspaces/${ws!.id}/members`),
    enabled: !!ws,
    staleTime: 5 * 60 * 1000,
  });

  const [inviteCode, setInviteCode] = useState<string | null>(null);
  const [inviteExpiresAt, setInviteExpiresAt] = useState<number | null>(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  async function handleCreateInvite() {
    if (!ws) return;
    setInviteLoading(true);
    setInviteError(null);
    try {
      const result = await api.post<{ code: string; expires_at: number }>(
        `/workspaces/${ws.id}/invites`,
      );
      setInviteCode(result.code);
      setInviteExpiresAt(result.expires_at);
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : t('workspace.inviteError'));
    } finally {
      setInviteLoading(false);
    }
  }

  async function handleCopyLink() {
    if (!inviteCode) return;
    const link = `${window.location.origin}/join?code=${inviteCode}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success(t('workspace.inviteCopied'));
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard blocked — code is still visible on screen
      toast.error(t('common.saveFailed', { error: 'clipboard' }));
    }
  }

  const expiresInDays =
    inviteExpiresAt != null
      ? Math.max(0, Math.floor((inviteExpiresAt - Math.floor(Date.now() / 1000)) / 86400))
      : null;

  return (
    <div className="min-h-screen bg-muted/30">
      <header className="border-b bg-background">
        <div className="container flex items-center gap-3 py-4">
          <BackButton to="/settings" />
          <div>
            <h1 className="text-lg font-semibold">{t('workspace.title')}</h1>
            <p className="text-xs text-muted-foreground">{t('workspace.subtitle')}</p>
          </div>
        </div>
      </header>

      <main className="container max-w-2xl space-y-3 py-6">
        {/* Workspace info */}
        <div className="rounded-2xl border bg-card p-4">
          <h2 className="text-sm font-semibold">{t('workspace.infoCard')}</h2>
          <dl className="mt-3 space-y-2 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">{t('workspace.name')}</dt>
              <dd className="text-right font-medium">{ws?.name ?? '—'}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">{t('workspace.role')}</dt>
              <dd className="text-right font-medium">
                {ws?.role === 'owner' ? t('workspace.roleOwner') : t('workspace.roleMember')}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">{t('workspace.currency')}</dt>
              <dd className="text-right font-medium">{ws?.default_currency ?? '—'}</dd>
            </div>
          </dl>
        </div>

        {/* Members */}
        <ListCard
          icon="👨‍👩‍👧"
          title={t('workspace.members')}
          summary={members ? t('workspace.membersCount', { count: members.length }) : undefined}
        >
          <div className="space-y-2 pt-1">
            {(members ?? []).map((m) => (
              <div
                key={m.id}
                className="flex items-center gap-3 rounded-lg border bg-background p-2.5"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-medium uppercase text-primary">
                  {(m.name ?? m.email).charAt(0)}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="line-clamp-2 text-sm font-medium">
                    {m.name ?? m.email}
                    {m.role === 'owner' && (
                      <span className="ml-2 rounded bg-amber-50 px-1.5 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                        👑 {t('workspace.roleOwner')}
                      </span>
                    )}
                  </div>
                  <div className="line-clamp-2 break-all text-xs text-muted-foreground">{m.email}</div>
                </div>
              </div>
            ))}
          </div>
        </ListCard>

        {/* Invite */}
        {!inviteCode ? (
          <div className="rounded-2xl border bg-card p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <h2 className="text-sm font-semibold">{t('workspace.inviteSection')}</h2>
                <p className="mt-1 text-xs text-muted-foreground">{t('workspace.inviteSubtitle')}</p>
              </div>
              <button
                onClick={handleCreateInvite}
                disabled={inviteLoading}
                className="shrink-0 rounded-md border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
              >
                {inviteLoading
                  ? t('workspace.inviteGenerating')
                  : t('workspace.inviteGenerate')}
              </button>
            </div>
            {inviteError && (
              <div className="mt-3 rounded-md bg-destructive/10 p-2 text-xs text-destructive">
                {inviteError}
              </div>
            )}
          </div>
        ) : (
          <div
            role="button"
            tabIndex={0}
            onClick={handleCopyLink}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleCopyLink();
              }
            }}
            className="block w-full cursor-pointer rounded-2xl border bg-gradient-to-br from-emerald-50 to-emerald-100/60 p-5 text-center transition active:scale-[0.99] dark:from-emerald-950/40 dark:to-emerald-900/30"
          >
            <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
              🎟️ {t('workspace.inviteSection')}
            </div>
            <div className="mt-2 font-mono text-3xl font-bold tabular-nums tracking-[0.2em] text-emerald-900 dark:text-emerald-100">
              {inviteCode}
            </div>
            <div className="mt-2 text-xs text-emerald-800/80 dark:text-emerald-200/80">
              {copied ? t('workspace.inviteCopied') : t('workspace.inviteTapHint')}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              {t('workspace.inviteExpiresInDays', { count: expiresInDays ?? 0 })}
            </div>
            {inviteError && (
              <div className="mt-3 rounded-md bg-destructive/10 p-2 text-xs text-destructive">
                {inviteError}
              </div>
            )}
            <div className="mt-3 flex items-center justify-center gap-2 text-xs text-muted-foreground">
              <span>{t('workspace.inviteRefresh')}:</span>
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCreateInvite();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    handleCreateInvite();
                  }
                }}
                className="cursor-pointer text-primary underline hover:opacity-70"
              >
                {inviteLoading ? '...' : t('workspace.inviteRefresh')}
              </span>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
